import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/customer-redux";

import { isRequestActive, loadAndTrack } from "redux-request-loading";
import { CustomMedicalType } from "@teamprevent/common/healthSurveillance/models";

import { ApplicationState } from "../../applicationState";

import { LOAD_CUSTOM_MEDICAL_TYPES } from "../actiontypes";
import { CustomMedicalTypeApi } from "../customMedicalTypesApi";
import { loadCustomMedicalTypesSuccess } from "../actions";

type useCustomMedicalsReturn =
    { customMedicalTypes: CustomMedicalType[]; isLoading: false; isError: false; refetch: () => Promise<void>; isLoaded: true }
    | { customMedicalTypes: undefined; isLoading: boolean; isError: boolean; refetch: () => Promise<void>; isLoaded: false };

/*
* This hook is used to load the custom medicals types from the server
* If you don't need the custom medical types straight away,
* you can pass `enable: false` to the hook to disable the automatic loading of the custom medical types
* until you need it by calling `refresh()`. This is the same pattern used by react-query to allow easier drop in replacement later
*/
export const useCustomMedicalTypes = ({ enable }: { enable?: boolean } = {}): useCustomMedicalsReturn => {
    const customMedicalTypes = useSelector((state: ApplicationState) => state.customMedicalTypes);
    const dispatch = useDispatch();
    const isLoading = useSelector<ApplicationState, boolean>(state => isRequestActive(state, LOAD_CUSTOM_MEDICAL_TYPES));
    const [isError, setIsError] = React.useState(false);
    const refetch = React.useCallback(async () => {
        if (isLoading) {
            return;
        }

        try {
            // TODO: Replace this with Redux Toolkit
            // @ts-expect-error TS2345
            await loadAndTrack(dispatch, LOAD_CUSTOM_MEDICAL_TYPES, (async () => {
                const result = await CustomMedicalTypeApi.getAll();
                dispatch(loadCustomMedicalTypesSuccess(result));
            })());
            setIsError(false);
        } catch (e) {
            setIsError(true);
            throw e;
        }
    }, [dispatch, isLoading]);
    React.useEffect(() => {
        if (enable === false
            || isLoading
            || isError
            || customMedicalTypes) {
            return;
        }

        refetch();
    }, [enable, isLoading, customMedicalTypes, refetch, isError]);

    if (customMedicalTypes) {
        return { customMedicalTypes, isLoading: false, isError: false, refetch, isLoaded: true };
    } else {
        return { customMedicalTypes: undefined, isLoading, isError, refetch, isLoaded: false };
    }
};
